import { useEffect, useMemo, useRef, useState } from "react";
import { useFestivalTheme } from "@/context/FestivalThemeContext";

const DEFAULT_END = "2026-11-12T18:00:00+05:30"; // 12 Nov 2026, 6 PM IST
const MILESTONES = [0.25, 0.5, 0.75];
const BURST_ANGLES = [-70, -35, -5, 25, 55, 90, 130, 170];

/** A burst of gold sparks at a fuse position (milestones + final-day crackle). */
function SparkBurst({ leftPct, small }) {
  const sparks = small ? BURST_ANGLES.slice(0, 4) : BURST_ANGLES;
  return (
    <div className="pq-burst" style={{ left: `clamp(34px, ${leftPct}%, calc(100% - 26px))` }} aria-hidden="true">
      {sparks.map((deg, i) => {
        const rad = (deg + Math.random() * 20 - 10) * (Math.PI / 180);
        const dist = (small ? 10 : 22) + Math.random() * (small ? 8 : 16);
        return (
          <span
            key={i}
            className={`pq-burst-spark ${small ? "pq-burst-spark-sm" : ""}`}
            style={{ "--dx": `${Math.cos(rad) * dist}px`, "--dy": `${Math.sin(rad) * dist}px` }}
          />
        );
      })}
    </div>
  );
}

/** Firecracker rocket removed per owner request (A3) — the bar keeps its
 *  countdown text, fuse progress line and milestone bursts only. */

function fmtCountdown(ms) {
  const mins = Math.max(0, Math.floor(ms / 60000));
  const d = Math.floor(mins / 1440);
  const h = Math.floor((mins % 1440) / 60);
  const m = mins % 60;
  return `${d}d ${String(h).padStart(2, "0")}h ${String(m).padStart(2, "0")}m`;
}

function fmtDeadline(endMs) {
  const s = new Intl.DateTimeFormat("en-IN", {
    day: "numeric", month: "short", hour: "numeric", minute: "2-digit",
    hour12: true, timeZone: "Asia/Kolkata",
  }).format(new Date(endMs));
  return `${s} IST`;
}

/**
 * RocketBar — pinned STOCK UP FEST countdown while a festival theme with the
 * rocket bar is live. The rocket creeps left→right from theme activation to
 * the fest deadline along a burning fuse; the scheduler switches the theme
 * off when time runs out. Ticks once a minute; motion respects reduced-motion.
 */
export default function RocketBar() {
  const { theme, decor, content } = useFestivalTheme();
  const enabled = Boolean(theme && decor.rocket_bar);
  const endMs = useMemo(() => {
    const t = new Date(content.fest_ends_at || DEFAULT_END).getTime();
    return Number.isFinite(t) ? t : null;
  }, [content.fest_ends_at]);
  const startMs = useMemo(() => {
    if (!endMs) return 0;
    const s = theme?.activated_at ? new Date(theme.activated_at).getTime() : NaN;
    return Number.isFinite(s) && s < endMs ? s : endMs - 30 * 86400000;
  }, [theme?.activated_at, endMs]);

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return undefined;
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, [enabled]);

  const reducedMotion = useRef(
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  const [bursts, setBursts] = useState([]);
  const fireBurst = (leftPct, small = false) => {
    if (reducedMotion.current) return;
    const id = Math.random().toString(36).slice(2);
    setBursts((b) => [...b, { id, leftPct, small }]);
    setTimeout(() => setBursts((b) => b.filter((x) => x.id !== id)), 1300);
  };

  const ended = enabled && endMs ? now >= endMs : false;
  const pct = enabled && endMs ? Math.min(1, Math.max(0, (now - startMs) / (endMs - startMs))) : 0;
  const finalDay = enabled && endMs && !ended && endMs - now < 24 * 3600 * 1000;

  // Milestone bursts: celebrate each 25/50/75% crossing once per session
  // (on load if already past, and live as the rocket crosses).
  useEffect(() => {
    if (!enabled || !endMs || ended) return;
    MILESTONES.forEach((m, i) => {
      const key = `pq_rocket_burst_${m}`;
      if (pct >= m && !sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, "1");
        setTimeout(() => fireBurst(m * 100), 600 + i * 450);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, endMs, pct, ended]);

  // Final-day crackle: soft pops near the flame while the last 24h burn down
  useEffect(() => {
    if (!finalDay || reducedMotion.current) return undefined;
    let timer;
    const crackle = () => {
      fireBurst(pct * 100 + (Math.random() * 2 - 1), true);
      timer = setTimeout(crackle, 2200 + Math.random() * 2000);
    };
    timer = setTimeout(crackle, 1200);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalDay]);

  // Shift the fixed navbar + page content down by the bar height while present
  useEffect(() => {
    document.documentElement.dataset.pqRocket = enabled ? "true" : "";
    return () => { document.documentElement.dataset.pqRocket = ""; };
  }, [enabled]);

  if (!enabled || !endMs) return null;

  return (
    <div
      data-testid="rocket-bar"
      className="fixed inset-x-0 top-0 z-[70] h-[var(--pq-rocket-h)] border-b border-amber-500/15 bg-[#0A0A10]"
    >
      <div className="mx-auto flex h-[calc(var(--pq-rocket-h)-10px)] max-w-7xl items-center justify-between gap-3 px-4 sm:px-6 lg:px-12">
        <p className="flex min-w-0 items-baseline gap-2 text-[11px] sm:text-sm">
          <span className="shrink-0 font-extrabold uppercase tracking-[0.18em] text-amber-500" data-testid="rocket-bar-label">
            Stock Up Fest
          </span>
          {ended ? (
            <span className="truncate font-semibold text-white/90" data-testid="rocket-bar-ended">
              The fest has ended — thank you for celebrating with us!
            </span>
          ) : (
            <>
              <span className="truncate text-white/70">
                ends in <span className="font-bold tabular-nums text-white" data-testid="rocket-bar-countdown">{fmtCountdown(endMs - now)}</span>
              </span>
              <span className="hidden shrink-0 text-white/40 sm:inline" data-testid="rocket-bar-deadline">
                · {fmtDeadline(endMs)}
              </span>
            </>
          )}
        </p>
        <p className="hidden shrink-0 text-[10px] font-bold uppercase tracking-[0.2em] text-white/35 md:block">
          Diwali Sale Event
        </p>
      </div>
      {/* fuse strip — rocket rides this line, well clear of the text row */}
      <div className="relative h-[10px]">
        <div className="absolute inset-x-0 top-1/2 h-[3px] -translate-y-1/2 bg-white/10">
          <div
            className="h-full bg-gradient-to-r from-amber-700 via-amber-500 to-amber-300 shadow-[0_0_8px_rgba(255,159,28,0.8)] transition-[width] duration-1000"
            style={{ width: `${pct * 100}%` }}
            data-testid="rocket-bar-burned"
          />
        </div>
        {bursts.map((b) => <SparkBurst key={b.id} leftPct={b.leftPct} small={b.small} />)}
      </div>
    </div>
  );
}
